import React, { useState, useEffect, useRef } from 'react';
import AddressAutocomplete from '../../../../shared/components/AddressAutocomplete';
import GoogleMapPicker from '../../../../shared/components/GoogleMapPicker';
import { geocodingApi } from '../../../../shared/api/geocoding.api';
import './StoreLocationEditor.css';

/**
 * Store Location Editor Component
 *
 * Comprehensive location picker for store setup/profile.
 * Allows store admins to set exact store location with lat/lng coordinates
 * using Google Places Autocomplete and interactive map.
 *
 * Part of Store Location Editor feature (STORE_LOCATION_EDITOR_ENTERPRISE_PLAN).
 *
 * @param {Object} props
 * @param {string} props.pincode - Store pincode (6 digits, required)
 * @param {string} props.address - Initial address value
 * @param {number} props.latitude - Initial latitude
 * @param {number} props.longitude - Initial longitude
 * @param {function} props.onChange - Called when location changes, emits {address, latitude, longitude}
 * @param {boolean} props.disabled - Whether editor is disabled
 * @param {function} props.onError - Called when validation error occurs
 * @param {function} props.onValidationChange - Called when the current location validation state changes
 *
 * @example
 * <StoreLocationEditor
 *   pincode="500039"
 *   address="Shop 23, Road 2, Boduppal, Hyderabad"
 *   latitude={17.421268}
 *   longitude={78.588932}
 *   onChange={({ address, latitude, longitude }) => {
 *     console.log('Location updated:', address, latitude, longitude);
 *   }}
 *   onError={(error) => console.error(error)}
 * />
 */
const StoreLocationEditor = ({
  pincode,
  address = '',
  latitude = null,
  longitude = null,
  onChange,
  disabled = false,
  onError,
  onValidationChange
}) => {
  // Local state for form values
  const [localAddress, setLocalAddress] = useState(address);
  const [localLat, setLocalLat] = useState(latitude);
  const [localLng, setLocalLng] = useState(longitude);

  // UI state
  const [hasManualEdit, setHasManualEdit] = useState(false);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [validationSuccess, setValidationSuccess] = useState(false);
  const [pincodeCenter, setPincodeCenter] = useState(null);
  const [loadingPincodeCenter, setLoadingPincodeCenter] = useState(false);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  // Sync external prop changes to local state
  useEffect(() => {
    setLocalAddress(address);
  }, [address]);

  useEffect(() => {
    setLocalLat(latitude);
  }, [latitude]);

  useEffect(() => {
    setLocalLng(longitude);
  }, [longitude]);

  // Fetch pincode center coordinates for map default location
  useEffect(() => {
    if (!pincode || pincode.length !== 6) {
      setPincodeCenter(null);
      setLoadingPincodeCenter(false);
      return;
    }

    setLoadingPincodeCenter(true);

    // Use Google Geocoding API to get pincode center (no fallback - must be accurate)
    geocodingApi.geocodeAddress(pincode, pincode)
      .then(result => {
        setPincodeCenter({
          lat: result.latitude,
          lng: result.longitude
        });
      })
      .catch(error => {
        // If pincode geocoding fails, show error instead of wrong location
        setPincodeCenter(null);
        const errorMsg = `Unable to locate pincode ${pincode} on map. Please verify the pincode is correct.`;
        setValidationError(errorMsg);
        if (onErrorRef.current) {
          onErrorRef.current(errorMsg);
        }
      })
      .finally(() => {
        setLoadingPincodeCenter(false);
      });
  }, [pincode]);

  return (
    <div className="store-location-editor">
      {/* Pincode Section (Read-only) */}
      <div className="editor-section">
        <label className="editor-label">
          Pincode
          <span className="required-indicator">*</span>
        </label>
        <input
          type="text"
          value={pincode || ''}
          disabled
          className="editor-input pincode-input"
          placeholder="Enter pincode"
        />
        <p className="editor-hint">
          Pincode is set during store registration. Contact support to change it.
        </p>
      </div>

      {/* Address Autocomplete Section */}
      <div className="editor-section">
        <label className="editor-label">
          Store Address
          <span className="required-indicator">*</span>
        </label>
        <AddressAutocomplete
          value={localAddress}
          locationBias={pincodeCenter ? { lat: pincodeCenter.lat, lng: pincodeCenter.lng, radius: 10000 } : null}
          onChange={(e) => {
            setLocalAddress(e.target.value);
            setHasManualEdit(true);
            setValidationSuccess(false);
            setValidationError(null);
            onValidationChange?.({
              isValid: false,
              error: 'Address was modified. Please geocode it before saving.'
            });
            // Clear coordinates when user manually edits (Scenario B from feature brief)
            setLocalLat(null);
            setLocalLng(null);

            // Propagate null coordinates to parent to prevent stale data in save payload
            if (onChange) {
              onChange({
                address: e.target.value,
                latitude: null,
                longitude: null
              });
            }
          }}
          onPlaceSelected={(place) => {
            setLocalAddress(place.address);
            setLocalLat(place.latitude);
            setLocalLng(place.longitude);
            setHasManualEdit(false);
            setValidationSuccess(true);
            setValidationError(null);
            onValidationChange?.({ isValid: true, error: null });

            // Emit to parent
            if (onChange) {
              onChange({
                address: place.address,
                latitude: place.latitude,
                longitude: place.longitude
              });
            }
          }}
          disabled={disabled}
          placeholder="Search for your store address..."
          className="address-input"
        />
        <p className="editor-hint">
          Start typing your store address. Select from suggestions for accurate coordinates.
        </p>
      </div>

      {/* Map Section */}
      <div className="editor-section">
        <label className="editor-label">Location on Map</label>
        {(localLat && localLng) || pincodeCenter ? (
          <GoogleMapPicker
            latitude={localLat || pincodeCenter?.lat}
            longitude={localLng || pincodeCenter?.lng}
            zoom={localLat && localLng ? 18 : 14}
            showMarker={!!(localLat && localLng)}
            onMapClick={async ({ latitude, longitude }) => {
              // User clicked on map to place marker
              setLocalLat(latitude);
              setLocalLng(longitude);
              setIsGeocoding(true);
              setValidationError(null);
              setHasManualEdit(false);

              try {
                // Call backend reverse geocoding API via centralized client
                const result = await geocodingApi.reverseGeocode(latitude, longitude);

                const newAddress = result.address;
                const newPincode = result.pincode;

                setLocalAddress(newAddress);

                // Validate pincode matches
                if (newPincode && pincode && newPincode !== pincode) {
                  const errorMsg = `Location is in pincode ${newPincode}, but your store is in pincode ${pincode}. Please click closer to your pincode area.`;
                  setValidationError(errorMsg);
                  setValidationSuccess(false);
                  onValidationChange?.({ isValid: false, error: errorMsg });
                  if (onError) {
                    onError(errorMsg);
                  }
                } else {
                  setValidationError(null);
                  setValidationSuccess(true);
                  onValidationChange?.({ isValid: true, error: null });

                  // Emit to parent
                  if (onChange) {
                    onChange({
                      address: newAddress,
                      latitude,
                      longitude
                    });
                  }
                }
              } catch (error) {
                const errorMsg = error?.data?.message || error?.message || 'Failed to get address for this location. Please check your connection and try again.';
                setValidationError(errorMsg);
                setValidationSuccess(false);
                onValidationChange?.({ isValid: false, error: errorMsg });
                if (onError) {
                  onError(errorMsg);
                }
              } finally {
                setIsGeocoding(false);
              }
            }}
            onMarkerDragEnd={async ({ latitude, longitude }) => {
              setLocalLat(latitude);
              setLocalLng(longitude);
              setIsGeocoding(true);
              setValidationError(null);

              try {
                // Call backend reverse geocoding API via centralized client
                const result = await geocodingApi.reverseGeocode(latitude, longitude);

                const newAddress = result.address;
                const newPincode = result.pincode;

                setLocalAddress(newAddress);

                // Validate pincode matches
                if (newPincode && pincode && newPincode !== pincode) {
                  const errorMsg = `Location is in pincode ${newPincode}, but your store is in pincode ${pincode}. Please adjust the marker or change your pincode.`;
                  setValidationError(errorMsg);
                  setValidationSuccess(false);
                  onValidationChange?.({ isValid: false, error: errorMsg });
                  if (onError) {
                    onError(errorMsg);
                  }
                } else {
                  setValidationError(null);
                  setValidationSuccess(true);
                  onValidationChange?.({ isValid: true, error: null });

                  // Emit to parent
                  if (onChange) {
                    onChange({
                      address: newAddress,
                      latitude,
                      longitude
                    });
                  }
                }
              } catch (error) {
                const errorMsg = error?.data?.message || error?.message || 'Failed to get address for this location. Please check your connection and try again.';
                setValidationError(errorMsg);
                setValidationSuccess(false);
                onValidationChange?.({ isValid: false, error: errorMsg });
                if (onError) {
                  onError(errorMsg);
                }
              } finally {
                setIsGeocoding(false);
              }
            }}
            height="400px"
          />
        ) : loadingPincodeCenter ? (
          <div className="map-placeholder">
            <p>Loading map...</p>
          </div>
        ) : (
          <div className="map-placeholder">
            <p>Enter a valid 6-digit pincode to view the map</p>
          </div>
        )}
        <p className="editor-hint">
          {localLat && localLng
            ? 'Drag the marker to adjust the location, or click anywhere on the map to place a new marker.'
            : pincodeCenter
            ? 'Click anywhere on the map to place your store location marker.'
            : 'The map will appear once you have a valid pincode.'}
        </p>
        {isGeocoding && (
          <p className="geocoding-status">
            Getting address for new location...
          </p>
        )}
      </div>

      {/* Geocode Button Section */}
      {hasManualEdit && !localLat && localAddress && (
        <div className="editor-section">
          <button
            className="geocode-button"
            onClick={async () => {
              setIsGeocoding(true);
              setValidationError(null);

              try {
                // Call backend geocoding API via centralized client (backend validates pincode)
                const result = await geocodingApi.geocodeAddress(localAddress, pincode);

                // Defensive check: validate pincode in result (backend should already enforce this)
                if (result.pincode && pincode && result.pincode !== pincode) {
                  const errorMsg = `Geocoded address is in pincode ${result.pincode}, but your store is in pincode ${pincode}. Please refine your address.`;
                  setValidationError(errorMsg);
                  setValidationSuccess(false);
                  onValidationChange?.({ isValid: false, error: errorMsg });
                  if (onError) {
                    onError(errorMsg);
                  }
                  return;
                }

                setLocalLat(result.latitude);
                setLocalLng(result.longitude);
                setLocalAddress(result.formattedAddress);
                setHasManualEdit(false);
                setValidationSuccess(true);
                setValidationError(null);
                onValidationChange?.({ isValid: true, error: null });

                // Emit to parent
                if (onChange) {
                  onChange({
                    address: result.formattedAddress,
                    latitude: result.latitude,
                    longitude: result.longitude
                  });
                }
              } catch (error) {
                const errorMsg = error?.data?.message || error?.message || 'Failed to get coordinates for this address. Please check your connection and try again.';
                setValidationError(errorMsg);
                setValidationSuccess(false);
                onValidationChange?.({ isValid: false, error: errorMsg });
                if (onError) {
                  onError(errorMsg);
                }
              } finally {
                setIsGeocoding(false);
              }
            }}
            disabled={isGeocoding || !localAddress || disabled}
          >
            {isGeocoding ? 'Geocoding...' : 'Geocode Address'}
          </button>
          <p className="editor-hint geocode-hint">
            Click to get coordinates for the address you entered.
          </p>
        </div>
      )}

      {/* Validation Messages Section */}
      <div className="validation-banners">
        {/* Warning: Manual edit without coordinates */}
        {hasManualEdit && !localLat && localAddress && (
          <div className="banner banner-warning">
            <span className="banner-text">
              Address was modified. Click 'Geocode Address' button below to get coordinates and enable saving.
            </span>
          </div>
        )}

        {/* Success: Validation successful */}
        {validationSuccess && !validationError && localLat && localLng && (
          <div className="banner banner-success">
            <span className="banner-text">
              Coordinates updated. Location validated. You can now save.
            </span>
          </div>
        )}

        {/* Error: Validation failed */}
        {validationError && (
          <div className="banner banner-error">
            <span className="banner-text">
              {validationError}
            </span>
          </div>
        )}

        {/* Info: Geocoding in progress */}
        {isGeocoding && (
          <div className="banner banner-info">
            <span className="banner-text">
              Getting coordinates for your address...
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default StoreLocationEditor;
